import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { AGENCES_SERVICES } from 'app/classesTs/agences-services';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import Swal from 'sweetalert2';
import { filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-listeagenceservice',
  templateUrl: './listeagenceservice.component.html',
  styleUrls: ['./listeagenceservice.component.scss']
})
export class ListeagenceserviceComponent implements OnInit, OnDestroy {
  // Agency Service Data
  allServices: any[] = [];
  filteredServices: any[] = [];
  
  // Filtering & Search
  selectedType: string = 'all';
  searchTerm: string = '';
  services = AGENCES_SERVICES;
  
  // Pagination with URL persistence
  currentPage: number = 1;
  itemsPerPage: number = 7;
  totalItems: number = 0;
  
  // Loading State
  isLoading: boolean = false;
  errorMessage: string = '';
  
  // Router subscription
  private routerSubscription: Subscription;
  
  // Data loaded flag
  dataLoaded: boolean = false;

  constructor(
    private agenciesService: AgenciesService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location
  ) {
    // Listen to route changes to restore state
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.restoreStateFromUrl();
      });
  }

  ngOnInit(): void {
    // First restore state from URL, then load data
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      this.restoreStateFromUrl();
      this.loadAllAgencies();
    });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Restore state from URL parameters
   */
  private restoreStateFromUrl(): void {
    this.route.queryParams.subscribe(params => {
      this.currentPage = params['page'] ? +params['page'] : 1;
      this.selectedType = params['type'] || 'all';
      this.searchTerm = params['search'] || '';
    });
  }

  /**
   * Update URL with current state
   */
  private updateUrlState(): void {
    const queryParams: any = {};
    
    if (this.currentPage !== 1) queryParams['page'] = this.currentPage;
    if (this.selectedType !== 'all') queryParams['type'] = this.selectedType;
    if (this.searchTerm.trim()) queryParams['search'] = this.searchTerm;

    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge'
    });

    this.location.replaceState(urlTree.toString());
  }

  /**
   * Load ALL agencies and filter for services
   */
  loadAllAgencies(): void {
    this.isLoading = true;
    this.errorMessage = '';
    
    // Use getListAgenceImmob() which should return ALL agencies
    this.agenciesService.getListAgenceImmob().subscribe({
      next: (response: any) => {
        this.filterAgenciesForServices(response);
      },
      error: (error) => {
        this.handleApiError(error);
      }
    });
  }

  /**
   * Filter agencies to get only service agencies
   */
  private filterAgenciesForServices(response: any): void {
    try {
      let allAgencies: any[] = [];
      
      if (Array.isArray(response)) {
        allAgencies = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        allAgencies = response.data;
      } else if (response && typeof response === 'object') {
        const arrayValues = Object.values(response).filter(val => Array.isArray(val));
        allAgencies = arrayValues.length > 0 ? arrayValues[0] as any[] : [];
      }
      
      // Filter for service agencies - check various possibilities
      const serviceAgencies = allAgencies.filter(agency => {
        // Check role
        const role = (agency.role || '').toLowerCase();
        const hasServiceRole = 
          role === 'agenceservice' ||
          role.includes('service') ||
          role.includes('agence') ||
          role === 'services' ||
          role === 'agency';
        
        // Check roles array
        const hasServiceInRoles = agency.roles && 
          Array.isArray(agency.roles) && 
          agency.roles.some((r: any) => {
            const rRole = (r.role || '').toLowerCase();
            return rRole === 'agenceservice' || 
                   rRole.includes('service') || 
                   rRole.includes('agence');
          });
        
        // Check categorie/type
        const categorie = (agency.categorie || '').toLowerCase();
        const type = (agency.type || '').toLowerCase();
        const hasServiceCategory = 
          this.services.some(s => s.value === categorie) ||
          this.services.some(s => s.value === type);
        
        return hasServiceRole || hasServiceInRoles || hasServiceCategory;
      });
      
      this.allServices = serviceAgencies;
      this.applyFilters();
      
      this.isLoading = false;
      this.dataLoaded = true;
    } catch (error) {
      this.errorMessage = 'Erreur lors du traitement des données';
      this.allServices = [];
      this.applyFilters();
      this.isLoading = false;
      this.dataLoaded = true;
    }
  }

  /**
   * Load services by type - ALTERNATIVE VERSION
   */
  loadServicesByType(typeId: string): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.selectedType = typeId;
    this.currentPage = 1;
    
    this.updateUrlState();
    
    if (typeId === 'all') {
      this.loadAllAgencies();
    } else {
      // Try the specific endpoint first
      this.agenciesService.getListByType(typeId).subscribe({
        next: (response: any) => {
          this.handleSpecificTypeResponse(response);
        },
        error: (error) => {
          // Fallback to filtering from all agencies
          this.loadAllAgencies();
        }
      });
    }
  }

  /**
   * Handle specific type response
   */
  private handleSpecificTypeResponse(response: any): void {
    try {
      let typeData: any[] = [];
      
      if (Array.isArray(response)) {
        typeData = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        typeData = response.data;
      } else if (response && typeof response === 'object') {
        const arrayValues = Object.values(response).filter(val => Array.isArray(val));
        typeData = arrayValues.length > 0 ? arrayValues[0] as any[] : [];
      }
      
      this.allServices = typeData;
      this.applyFilters();
      
      this.isLoading = false;
      this.dataLoaded = true;
    } catch (error) {
      this.errorMessage = 'Erreur lors du traitement des données';
      this.allServices = [];
      this.applyFilters();
      this.isLoading = false;
      this.dataLoaded = true;
    }
  }

  /**
   * Handle API error
   */
  private handleApiError(error: any): void {
    this.errorMessage = 'Erreur lors du chargement des services';
    this.isLoading = false;
    this.dataLoaded = true;
    this.allServices = [];
    this.applyFilters();
  }

  /**
   * Get count for a specific service type
   */
  getTypeCount(typeId: string): number {
    if (typeId === 'all') return this.allServices.length;
    
    return this.allServices.filter(service => {
      const serviceType = service.categorie || service.type || '';
      return serviceType.toLowerCase() === typeId.toLowerCase();
    }).length;
  }

  /**
   * Get total services count
   */
  getTotalServicesCount(): number {
    return this.allServices.reduce((total, service) => {
      return total + (service.services?.length || 0);
    }, 0);
  }

  /**
   * Apply filters (search only)
   */
  applyFilters(): void {
    let filtered = [...this.allServices];
    
    // Filter by type if not 'all'
    if (this.selectedType !== 'all') {
      filtered = filtered.filter(service => {
        const serviceType = service.categorie || service.type || '';
        return serviceType.toLowerCase() === this.selectedType.toLowerCase();
      });
    }
    
    // Filter by search term
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(service => {
        const nameMatch = service.name?.toLowerCase().includes(searchLower) || false;
        const addressMatch = service.adresse?.toLowerCase().includes(searchLower) || false;
        const emailMatch = service.email?.toLowerCase().includes(searchLower) || false;
        const phoneMatch = service.phone_number?.toString().includes(searchLower) || false;
        const roleMatch = service.role?.toLowerCase().includes(searchLower) || false;
        const typeMatch = service.categorie?.toLowerCase().includes(searchLower) || 
                         service.type?.toLowerCase().includes(searchLower) || false;
        return nameMatch || addressMatch || emailMatch || phoneMatch || roleMatch || typeMatch;
      });
    }
    
    this.filteredServices = filtered;
    this.totalItems = filtered.length;
    
    const maxPage = Math.ceil(this.totalItems / this.itemsPerPage);
    if (this.currentPage > maxPage && maxPage > 0) {
      this.currentPage = maxPage;
      this.updateUrlState();
    }
  }

  /**
   * Perform search
   */
  performSearch(): void {
    this.currentPage = 1;
    this.applyFilters();
    this.updateUrlState();
  }

  /**
   * Clear all filters
   */
  clearAllFilters(): void {
    this.selectedType = 'all';
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadAllAgencies();
    this.updateUrlState();
  }

  /**
   * Get type label from value
   */
  getTypeLabel(typeValue: string): string {
    if (!typeValue) return 'Non spécifié';
    
    const type = this.services.find(s => s.value === typeValue.toLowerCase());
    return type ? type.label : 
      typeValue.charAt(0).toUpperCase() + typeValue.slice(1);
  }

  /**
   * Handle page change
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.updateUrlState();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Handle page size change
   */
  onPageSizeChange(size: number): void {
    this.itemsPerPage = size;
    this.currentPage = 1;
    this.applyFilters();
    this.updateUrlState();
  }

  /**
   * Get display range for pagination info
   */
  getDisplayRange(): { start: number, end: number } {
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    return { start, end };
  }

  /**
   * Handle image error
   */
  handleImageError(event: any): void {
    event.target.src = 'assets/default-service.png';
  }

  /**
   * Delete service with confirmation
   */
  deleteService(service: any, index: number): void {
    if (!service.id || !service.name) return;
    
    Swal.fire({
      title: `Supprimer ${service.name}?`,
      text: "Cette action est irréversible et supprimera également tous les services associés.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff4757",
      cancelButtonColor: "#2ed573",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
      reverseButtons: true,
      backdrop: true,
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Suppression en cours...',
          text: 'Veuillez patienter',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        this.agenciesService.deleteItem(service.id).subscribe({
          next: () => {
            Swal.fire({
              title: "Supprimé!",
              text: "Le service a été supprimé avec succès.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false
            });
            
            const globalIndex = this.allServices.findIndex(s => s.id === service.id);
            if (globalIndex !== -1) {
              this.allServices.splice(globalIndex, 1);
            }
            
            const filteredIndex = this.filteredServices.findIndex(s => s.id === service.id);
            if (filteredIndex !== -1) {
              this.filteredServices.splice(filteredIndex, 1);
            }
            
            this.applyFilters();
          },
          error: (error) => {
            Swal.fire({
              title: "Erreur!",
              text: "Impossible de supprimer le service. Veuillez réessayer.",
              icon: "error"
            });
          }
        });
      }
    });
  }

  /**
   * Get status color based on active status
   */
  getStatusColor(active: boolean): string {
    return active ? '#2ecc71' : '#e74c3c';
  }

  /**
   * Get status label
   */
  getStatusLabel(active: boolean): string {
    return active ? 'Actif' : 'Inactif';
  }
}